// Data-confident variant of the Publish profiles page.
//
// Aesthetic commit: the bundle inspection IS the page. A wide hero
// panel holds the format chip, sha256 strip, total files (count-up),
// and a tall majors bar chart. The sources rail (uploader + previous
// bundles as selectable rows) lives in a compact left column, not
// inside a card. Release options collapse into a single committed
// footer bar that materializes once a bundle resolves, so the
// commit moment is always one click away.
//
// Motion: hero scales in from 0.98→1 on detection, the bar chart's
// columns transform-scale up from the baseline, file counts animate
// numerically, footer bar nudges up on first-valid form.

import { Badge } from "@veil/ui/components/badge";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import { UploadDropzone } from "@veil/ui/upload";
import { ArrowRight, FileArchive, Loader2 } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useMemo } from "react";
import { MajorBreakdown } from "@/components/profile-major-card";
import { EASE_OUT_EXPO } from "@/lib/motion";
import { AnimatedCounter } from "../parts/animated-counter";
import { ShaStrip } from "../parts/sha-strip";
import type { PublishProfilesController } from "../use-publish-profiles-controller";

const EASE = [0.22, 1, 0.36, 1] as const;
const ACCEPT = {
	"application/zip": [".zip"],
	"application/x-tar": [".tar"],
	"application/gzip": [".tar.gz", ".tgz"],
};

function formatLabel(fmt: string | null | undefined): string {
	switch (fmt) {
		case "raw-provider":
			return "Raw provider bundle";
		case "migrated-single-major":
			return "Migrated · single major";
		case "migrated-multi-major":
			return "Migrated · multi-major";
		case "migrated-flat":
			return "Migrated · flat";
		case "unknown":
			return "Unrecognized";
		case null:
		case undefined:
			return "Awaiting inspection";
		default:
			return String(fmt);
	}
}

export function DataConfidentView({
	controller: c,
}: {
	controller: PublishProfilesController;
}) {
	const reduce = useReducedMotion();

	const totalFiles = useMemo(() => {
		if (!c.preview) return 0;
		return (
			c.preview.fileCount ??
			c.preview.detectedMajors.reduce((acc, m) => acc + m.fileCount, 0)
		);
	}, [c.preview]);

	const chartData = useMemo(() => {
		if (!c.preview) return [];
		const total = c.preview.detectedMajors.reduce(
			(acc, m) => acc + m.fileCount,
			0,
		);
		if (!total) return [];
		return c.preview.detectedMajors.map((m) => ({
			major: m.major,
			files: m.fileCount,
			pct: m.fileCount / total,
		}));
	}, [c.preview]);

	const canSubmit =
		!!c.bundleId && !c.submitting && !c.formatBlocked && !c.majorMismatch;
	const showFooter = !!c.bundleId && !!c.preview && !c.formatBlocked;

	return (
		<motion.div
			initial={reduce ? false : { opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.32, ease: EASE }}
			className="flex flex-col gap-8 pb-32"
		>
			<header className="flex flex-col gap-2">
				<span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
					Releases · Profiles
				</span>
				<h1 className="text-3xl font-semibold leading-tight tracking-tight">
					Publish profiles
				</h1>
				<p className="max-w-prose text-sm text-muted-foreground">
					Verify the detected layout below, then publish from the footer.
				</p>
			</header>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[20rem_minmax(0,1fr)]">
				<aside className="flex flex-col gap-5">
					<div className="flex flex-col gap-3">
						<h2 className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
							Source
						</h2>
						<UploadDropzone
							state={c.upload.state}
							file={c.upload.file}
							stats={c.upload.stats}
							error={c.upload.error}
							accept={ACCEPT}
							onDrop={c.upload.start}
							onCancel={c.upload.cancel}
							onRetry={c.upload.retry}
							onReset={c.upload.reset}
							idleHint={
								<span>
									Drop a <code className="font-mono">.zip</code> /{" "}
									<code className="font-mono">.tar</code> /{" "}
									<code className="font-mono">.tar.gz</code>. Resumable.
								</span>
							}
							doneSummary={
								c.upload.bundle ? (
									<div className="flex items-center justify-between gap-3">
										<span className="font-mono tabular-nums">
											sha256:{c.upload.bundle.sha256.slice(0, 8)}…
										</span>
										<span className="tabular-nums">
											{formatBytes(c.upload.bundle.sizeBytes)}
										</span>
									</div>
								) : null
							}
						/>
					</div>

					<div className="flex flex-col gap-3">
						<div className="flex items-baseline justify-between">
							<h2 className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
								Recent bundles
							</h2>
							<span className="text-xs tabular-nums text-muted-foreground">
								{c.bundles.length}
							</span>
						</div>
						{c.bundles.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								No bundles uploaded yet.
							</p>
						) : (
							<div
								role="radiogroup"
								aria-label="Existing bundles"
								className="flex flex-col gap-1"
							>
								{c.bundles.slice(0, 8).map((b) => {
									const active = b.id === c.bundleId;
									return (
										<div key={b.id}>
											{/* biome-ignore lint/a11y/useSemanticElements: native radio cannot host rich child layout */}
											<button
												type="button"
												role="radio"
												aria-checked={active}
												onClick={() => c.selectBundle(b.id)}
												className={
													active
														? "group flex w-full items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-left transition-all"
														: "group flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left transition-all hover:border-border hover:bg-muted/50"
												}
											>
												<FileArchive
													className={
														active
															? "size-4 shrink-0 text-primary"
															: "size-4 shrink-0 text-muted-foreground"
													}
												/>
												<div className="flex min-w-0 flex-1 flex-col">
													<span className="truncate font-mono text-xs">
														{b.filename}
													</span>
													<span className="text-[11px] tabular-nums text-muted-foreground">
														{formatBytes(b.sizeBytes)} ·{" "}
														{formatRelative(b.uploadedAt)}
													</span>
												</div>
											</button>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</aside>

				<section className="min-w-0">
					<AnimatePresence mode="wait">
						{!c.bundleId ? (
							<motion.div
								key="empty"
								initial={reduce ? false : { opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.25, ease: EASE }}
								className="flex h-full min-h-72 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-muted/20 p-12 text-center"
							>
								<FileArchive className="size-8 text-muted-foreground/60" />
								<p className="text-sm font-medium">No bundle selected</p>
								<p className="max-w-xs text-xs text-muted-foreground">
									Upload a raw provider bundle on the left, or pick one of your
									recent uploads. The inspection panel will open here.
								</p>
							</motion.div>
						) : c.previewLoading ? (
							<motion.div
								key="loading"
								initial={reduce ? false : { opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2, ease: EASE }}
								className="flex h-full min-h-72 flex-col items-center justify-center gap-2 rounded-2xl bg-card p-12 ring-1 ring-foreground/10"
							>
								<Loader2 className="size-6 animate-spin text-muted-foreground" />
								<p className="text-sm text-muted-foreground">
									Reading bundle layout…
								</p>
							</motion.div>
						) : c.previewError ? (
							<motion.div
								key="error"
								initial={reduce ? false : { opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2, ease: EASE }}
								className="rounded-2xl bg-destructive/5 p-6 ring-1 ring-destructive/30"
							>
								<p className="text-sm text-destructive">{c.previewError}</p>
							</motion.div>
						) : c.preview ? (
							<motion.div
								key="hero"
								initial={reduce ? false : { opacity: 0, scale: 0.98, y: 8 }}
								animate={{ opacity: 1, scale: 1, y: 0 }}
								exit={{ opacity: 0, scale: 0.99 }}
								transition={{ duration: 0.42, ease: EASE_OUT_EXPO }}
								className="flex flex-col gap-6 overflow-hidden rounded-2xl bg-card p-6 ring-1 ring-foreground/10"
							>
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div className="flex flex-col gap-1.5">
										<div className="flex flex-wrap items-center gap-2">
											<Badge
												variant={
													c.formatBlocked
														? "destructive"
														: c.preview.detectedFormat === "raw-provider"
															? "secondary"
															: "success"
												}
											>
												{formatLabel(c.preview.detectedFormat)}
											</Badge>
											{c.preview.inspectionError ? (
												<Badge variant="destructive">Inspection error</Badge>
											) : null}
										</div>
										<p className="font-mono text-xs text-muted-foreground">
											{c.preview.filename}
										</p>
									</div>
									<div className="text-right">
										<div className="text-3xl font-semibold tabular-nums leading-none">
											<AnimatedCounter value={totalFiles} />
										</div>
										<div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
											files · {formatBytes(c.preview.sizeBytes)}
										</div>
									</div>
								</div>

								<ShaStrip
									filename={c.preview.filename}
									sha256={
										c.upload.bundle?.sha256 ??
										c.bundles.find((b) => b.id === c.preview?.id)?.sha256 ??
										""
									}
									sizeBytes={c.preview.sizeBytes}
								/>

								{chartData.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										No detected majors. The pipeline cannot publish an unknown
										layout.
									</p>
								) : (
									<MajorsBarChart data={chartData} reduce={!!reduce} />
								)}

								{c.preview.detectedMajors.length > 0 ? (
									<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
										{c.preview.detectedMajors.map((m, i) => (
											<motion.div
												key={m.major}
												initial={reduce ? false : { opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{
													duration: 0.32,
													ease: EASE,
													delay: 0.1 + i * 0.04,
												}}
											>
												<MajorBreakdown major={m} />
											</motion.div>
										))}
									</div>
								) : null}
							</motion.div>
						) : null}
					</AnimatePresence>
				</section>
			</div>

			<AnimatePresence>
				{showFooter ? (
					<motion.form
						key="commit"
						onSubmit={c.onSubmit}
						initial={reduce ? false : { opacity: 0, y: 28 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 20 }}
						transition={{ duration: 0.38, ease: EASE }}
						className="sticky bottom-4 z-30 mx-auto flex w-full max-w-4xl items-center gap-3 rounded-2xl border border-border/80 bg-background/95 px-4 py-3 shadow-xl shadow-foreground/5 backdrop-blur sm:gap-4 sm:px-5 supports-[backdrop-filter]:bg-background/85"
					>
						<div className="hidden flex-col sm:flex">
							<span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
								Commit
							</span>
							<span className="text-xs text-muted-foreground">Release</span>
						</div>

						<div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
							<MajorPicker controller={c} />
							<span className="text-muted-foreground/60">·</span>
							<BumpPicker controller={c} />
						</div>

						{c.majorMismatch ? (
							<span className="hidden text-xs text-destructive lg:block">
								major not in bundle
							</span>
						) : null}

						<button
							type="submit"
							disabled={!canSubmit}
							className={
								canSubmit
									? "group inline-flex h-9 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-all hover:translate-y-[-1px] hover:bg-primary/90 disabled:opacity-50"
									: "inline-flex h-9 shrink-0 items-center gap-2 rounded-lg bg-muted px-4 text-sm font-medium text-muted-foreground"
							}
						>
							{c.submitting ? (
								<Loader2 className="size-4 animate-spin" />
							) : null}
							{c.submitting ? "Starting" : "Start release"}
							{!c.submitting ? (
								<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
							) : null}
						</button>
					</motion.form>
				) : null}
			</AnimatePresence>
		</motion.div>
	);
}

function MajorPicker({
	controller: c,
}: {
	controller: PublishProfilesController;
}) {
	if (c.detectedMajors.length === 0) {
		return (
			<div className="flex min-w-0 items-baseline gap-1.5">
				<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
					major
				</span>
				<span className="font-mono text-sm text-muted-foreground">—</span>
			</div>
		);
	}
	return (
		<label className="flex min-w-0 items-baseline gap-1.5">
			<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
				major
			</span>
			<select
				value={c.browserMajor ? String(c.browserMajor) : ""}
				onChange={(e) => c.setBrowserMajor(parseInt(e.target.value, 10))}
				className="rounded-md border border-border bg-background px-2 py-1 font-mono text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
			>
				{c.detectedMajors.map((m) => (
					<option key={m} value={String(m)}>
						{m}
					</option>
				))}
			</select>
		</label>
	);
}

function BumpPicker({
	controller: c,
}: {
	controller: PublishProfilesController;
}) {
	const reduce = useReducedMotion();
	return (
		<div className="flex items-baseline gap-1.5">
			<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
				bump
			</span>
			<div
				role="radiogroup"
				aria-label="Bump type"
				className="inline-flex h-7 rounded-md border border-border bg-background p-0.5"
			>
				{(["patch", "minor", "major"] as const).map((b) => {
					const active = b === c.bumpType;
					return (
						// biome-ignore lint/a11y/useSemanticElements: native radio cannot host the animated pill background
						<button
							key={b}
							type="button"
							role="radio"
							aria-checked={active}
							onClick={() => c.setBumpType(b)}
							className={
								active
									? "relative inline-flex items-center justify-center rounded px-2 text-[11px] font-medium tracking-wide capitalize"
									: "relative inline-flex items-center justify-center rounded px-2 text-[11px] font-medium tracking-wide capitalize text-muted-foreground hover:text-foreground"
							}
						>
							{active && !reduce ? (
								<motion.span
									layoutId="dc-bump-pill"
									aria-hidden
									className="absolute inset-0 rounded bg-foreground/10"
									transition={{ duration: 0.26, ease: EASE }}
								/>
							) : active ? (
								<span
									aria-hidden
									className="absolute inset-0 rounded bg-foreground/10"
								/>
							) : null}
							<span className="relative">{b}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}

// Custom bar chart that animates via transform: scaleY so we don't
// animate layout properties. Each bar is a flex column with a label
// underneath; the bar itself is a scaled inner block.
function MajorsBarChart({
	data,
	reduce,
}: {
	data: Array<{ major: number; files: number; pct: number }>;
	reduce: boolean;
}) {
	const max = Math.max(...data.map((d) => d.pct), 0.0001);
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-baseline justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
				<span>Files per detected major</span>
				<span className="tabular-nums">{data.length} majors</span>
			</div>
			<div className="flex h-32 items-end gap-2 border-b border-border pb-1">
				{data.map((d, i) => {
					const heightRatio = d.pct / max;
					return (
						<div
							key={d.major}
							className="group flex h-full min-w-0 flex-1 flex-col items-center gap-1"
						>
							<div className="relative flex w-full flex-1 items-end overflow-hidden">
								<motion.span
									className="block w-full origin-bottom rounded-t-sm bg-primary/80 group-hover:bg-primary"
									style={{ height: `${Math.max(2, heightRatio * 100)}%` }}
									initial={reduce ? false : { scaleY: 0 }}
									animate={{ scaleY: 1 }}
									transition={{
										duration: 0.6,
										ease: EASE_OUT_EXPO,
										delay: i * 0.06,
									}}
								/>
							</div>
							<span className="font-mono text-[10px] tabular-nums text-muted-foreground">
								{d.major}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}
